import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarDays, Power, PowerOff, RefreshCw, RotateCcw, Save, Sparkles } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageShell } from "@/pages/dashboard/RolePages";

const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Bogota" });
const blankLocale = () => ({
  headline: "", radar: "", diagnosis: "", shadow: "", virtue: "", wellbeing: "",
  money: "", work: "", emotional: "", love: "", tisane: "", frequency: "",
  message: "", energy_number: "", activation: "", closing: "", guidance: "",
});
const DETAIL_FIELDS = [
  ["radar", "Radar energético"],
  ["diagnosis", "Diagnóstico energético"],
  ["shadow", "Sombra activa"],
  ["virtue", "Virtud a activar"],
  ["wellbeing", "Salud y bienestar"],
  ["money", "Dinero"],
  ["work", "Trabajo y proyectos"],
  ["emotional", "Emocional"],
  ["love", "Amor y relaciones"],
  ["tisane", "Tisana del día"],
  ["frequency", "Frecuencia energética"],
  ["message", "Mensaje del día"],
  ["activation", "Frase de activación"],
  ["closing", "Clave del día"],
  ["guidance", "Nota orientativa"],
];
const blankEntry = () => ({ status: "draft", publish_at: "", content: { es: blankLocale(), en: blankLocale() } });
const toLocalInput = (value) => value ? new Date(value).toISOString().slice(0, 16) : "";

const HoroscopeManager = () => {
  const [date, setDate] = useState(today());
  const [settings, setSettings] = useState(null);
  const [items, setItems] = useState({});
  const [signKey, setSignKey] = useState("aries");
  const [lang, setLang] = useState("es");
  const [entry, setEntry] = useState(blankEntry());
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");

  const loadSettings = useCallback(async () => {
    const { data } = await api.get("/api/director/landing-vivo/settings");
    setSettings(data);
    setSignKey((current) => data.signs.some((sign) => sign.key === current) ? current : data.signs[0].key);
  }, []);

  const loadDay = useCallback(async () => {
    const { data } = await api.get("/api/director/landing-vivo/horoscopes", { params: { day: date } });
    setItems(data.items || {});
  }, [date]);

  useEffect(() => {
    Promise.all([loadSettings(), loadDay()]).catch((e) => setError(formatApiError(e)));
  }, [loadSettings, loadDay]);

  useEffect(() => {
    const source = items[signKey];
    setEntry(source ? {
      status: source.status,
      publish_at: toLocalInput(source.publish_at),
      content: {
        es: { ...blankLocale(), ...(source.content?.es || {}) },
        en: { ...blankLocale(), ...(source.content?.en || {}) },
      },
    } : blankEntry());
  }, [items, signKey]);

  const signs = useMemo(() => [...(settings?.signs || [])].sort((a, b) => a.display_order - b.display_order), [settings]);
  const currentSign = signs.find((sign) => sign.key === signKey);
  const patchLocale = (key, value) => setEntry((current) => ({
    ...current,
    content: { ...current.content, [lang]: { ...current.content[lang], [key]: value } },
  }));

  const save = async (forcedStatus) => {
    setBusy(forcedStatus || "save"); setError(""); setOk("");
    try {
      const payload = {
        ...entry,
        status: forcedStatus || entry.status,
        publish_at: (forcedStatus || entry.status) === "scheduled" && entry.publish_at
          ? new Date(entry.publish_at).toISOString()
          : null,
      };
      const { data } = await api.put(`/api/director/landing-vivo/horoscopes/${date}/${signKey}`, payload);
      setItems((current) => ({ ...current, [signKey]: data }));
      setOk("Horóscopo guardado correctamente.");
    } catch (e) { setError(formatApiError(e)); }
    finally { setBusy(""); }
  };

  const changeActivation = async (action) => {
    setBusy(action); setError(""); setOk("");
    try {
      const { data } = await api.post(`/api/director/landing-vivo/horoscopes/${date}/${signKey}/${action}`);
      setItems((current) => ({ ...current, [signKey]: data }));
      setOk(action === "activate" ? "Horóscopo activo en el landing." : "Horóscopo desactivado y conservado como borrador.");
    } catch (e) { setError(formatApiError(e)); }
    finally { setBusy(""); }
  };

  const restoreAutomatic = async () => {
    setBusy("auto"); setError(""); setOk("");
    try {
      const { data } = await api.delete(`/api/director/landing-vivo/horoscopes/${date}/${signKey}`);
      setItems((current) => ({ ...current, [signKey]: data }));
      setOk("Se eliminó la edición manual. Este signo vuelve a generarse automáticamente.");
    } catch (e) { setError(formatApiError(e)); }
    finally { setBusy(""); }
  };

  const saveSettings = async () => {
    setBusy("settings"); setError(""); setOk("");
    try {
      const { data } = await api.put("/api/director/landing-vivo/settings", {
        enabled: settings.enabled,
        auto_generate: settings.auto_generate !== false,
        content: settings.content,
        signs: settings.signs,
      });
      setSettings(data); setOk("Configuración de Landing Vivo guardada.");
    } catch (e) { setError(formatApiError(e)); }
    finally { setBusy(""); }
  };

  if (!settings) return <PageShell title="LANDING VIVO KD9" kicker="Director KD9"><div className="kd9-card p-8">Cargando…</div></PageShell>;
  const locale = entry.content[lang];

  return (
    <PageShell
      kicker="Director KD9 · Publicación diaria"
      title="LANDING VIVO KD9"
      desc="KD9 genera los 12 signos automáticamente cada día. Las publicaciones manuales del Director tienen prioridad por fecha y signo."
      badge={<button onClick={() => Promise.all([loadSettings(), loadDay()])} className="kd9-btn-outline p-2 rounded-lg"><RefreshCw size={15}/></button>}
    >
      {error && <div className="p-3 mb-4 border border-red-800 text-red-300 rounded-lg">{error}</div>}
      {ok && <div className="p-3 mb-4 border border-emerald-700 text-emerald-300 rounded-lg">{ok}</div>}

      <section className="kd9-card p-5 mb-5">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
          <div>
            <h2 className="font-cinzel kd9-gold-text text-xl">HORÓSCOPO POR FECHA</h2>
            <p className="text-sm mt-1" style={{ color: "var(--kd9-text-soft)" }}>Borrador, programación y publicación son independientes para cada signo.</p>
          </div>
          <label className="text-xs uppercase tracking-widest">Fecha
            <input type="date" className="kd9-input px-3 mt-2" value={date} onChange={(e) => setDate(e.target.value)} />
          </label>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {signs.map((sign) => {
            const status = items[sign.key]?.status || "empty";
            const source = items[sign.key]?.source || "manual";
            return <button key={sign.key} onClick={() => setSignKey(sign.key)} className={signKey === sign.key ? "kd9-btn-gold px-3 py-2 rounded-lg text-xs" : "kd9-btn-outline px-3 py-2 rounded-lg text-xs"}>
              {sign.icon} {sign.label_es}
              <span className="ml-2 opacity-70">· {source === "auto" ? "automático" : status === "empty" ? "sin cargar" : status}</span>
            </button>;
          })}
        </div>

        <div className="flex flex-wrap justify-between gap-3 mb-4">
          <div>
            <div className="font-cinzel text-lg">{currentSign?.icon} {currentSign?.label_es} · {date}</div>
            <div className="font-mont text-[9px] uppercase tracking-[.16em] mt-1" style={{ color: items[signKey]?.source === "auto" ? "var(--kd9-accent)" : "var(--kd9-text-soft)" }}>
              {items[signKey]?.source === "auto" ? "Generado automáticamente por KD9" : "Edición manual del Director"}
            </div>
          </div>
          <div className="flex gap-2">{["es", "en"].map((code) => <button key={code} onClick={() => setLang(code)} className={lang === code ? "kd9-btn-primary px-4 py-2 rounded" : "kd9-btn-outline px-4 py-2 rounded"}>{code.toUpperCase()}</button>)}</div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <label className="text-xs uppercase tracking-widest">Encabezado
            <input className="kd9-input px-3 mt-2" value={locale.headline} onChange={(e) => patchLocale("headline", e.target.value)} />
          </label>
          <label className="text-xs uppercase tracking-widest">Número energético
            <input className="kd9-input px-3 mt-2" maxLength={12} value={locale.energy_number} onChange={(e) => patchLocale("energy_number", e.target.value)} />
          </label>

          {DETAIL_FIELDS.map(([key, label]) => <label key={key} className="md:col-span-2 text-xs uppercase tracking-widest">{label}
            <textarea rows={key === "radar" || key === "diagnosis" || key === "message" ? 4 : 3} className="kd9-input p-3 mt-2 w-full" value={locale[key] || ""} onChange={(e) => patchLocale(key, e.target.value)} />
          </label>)}

          <label className="text-xs uppercase tracking-widest">Estado manual
            <select className="kd9-input px-3 mt-2" value={entry.status} onChange={(e) => setEntry((current) => ({ ...current, status: e.target.value }))}>
              <option value="draft">Borrador</option>
              <option value="scheduled">Programado</option>
              <option value="active">Activo</option>
            </select>
          </label>
          {entry.status === "scheduled" && <label className="text-xs uppercase tracking-widest">Publicar el
            <input type="datetime-local" className="kd9-input px-3 mt-2" value={entry.publish_at} onChange={(e) => setEntry((current) => ({ ...current, publish_at: e.target.value }))} />
          </label>}
        </div>

        <div className="flex flex-wrap gap-3 mt-6">
          <button disabled={Boolean(busy)} onClick={() => save()} className="kd9-btn-primary px-5 py-3 rounded-lg text-xs flex items-center gap-2"><Save size={14}/> Guardar</button>
          <button disabled={Boolean(busy)} onClick={() => changeActivation("activate")} className="kd9-btn-gold px-5 py-3 rounded-lg text-xs flex items-center gap-2"><Power size={14}/> Activar ahora</button>
          <button disabled={Boolean(busy) || items[signKey]?.source === "auto"} onClick={() => changeActivation("deactivate")} className="kd9-btn-outline px-5 py-3 rounded-lg text-xs flex items-center gap-2 disabled:opacity-50"><PowerOff size={14}/> Desactivar manual</button>
          {items[signKey]?.source !== "auto" && settings.auto_generate !== false && <button disabled={Boolean(busy)} onClick={restoreAutomatic} className="kd9-btn-outline px-5 py-3 rounded-lg text-xs flex items-center gap-2"><RotateCcw size={14}/> Volver a automático</button>}
          {items[signKey]?.source === "auto" && <span className="text-xs flex items-center gap-2" style={{ color: "var(--kd9-text-soft)" }}><Sparkles size={14}/> Si guardas este contenido, se convertirá en una edición manual para {date}.</span>}
          {entry.status === "scheduled" && items[signKey]?.source !== "auto" && <span className="text-xs flex items-center gap-2" style={{ color: "var(--kd9-text-soft)" }}><CalendarDays size={14}/> Se mostrará automáticamente al llegar la fecha y hora.</span>}
        </div>
      </section>

      <section className="kd9-card p-5">
        <div className="flex flex-wrap justify-between gap-3 mb-4">
          <div><h2 className="font-cinzel kd9-gold-text text-xl">PRESENTACIÓN DEL MÓDULO</h2><p className="text-sm mt-1" style={{ color: "var(--kd9-text-soft)" }}>Este contenido también proviene de esta administración.</p></div>
          <div className="flex flex-col sm:flex-row gap-3">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={settings.enabled} onChange={(e) => setSettings((current) => ({ ...current, enabled: e.target.checked }))}/> Visible en landing</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={settings.auto_generate !== false} onChange={(e) => setSettings((current) => ({ ...current, auto_generate: e.target.checked }))}/> Generación diaria automática</label>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          {["es", "en"].map((code) => <div key={code} className="rounded-xl border p-4" style={{ borderColor: "var(--kd9-border)" }}>
            <h3 className="font-cinzel mb-3">{code.toUpperCase()}</h3>
            {[
              ["kicker", "Etiqueta superior"], ["title", "Título"], ["subtitle", "Subtítulo"], ["empty_message", "Mensaje sin publicación"],
            ].map(([key, label]) => <label key={key} className="block text-xs uppercase tracking-widest mb-3">{label}
              <input className="kd9-input px-3 mt-2" value={settings.content[code][key]} onChange={(e) => setSettings((current) => ({ ...current, content: { ...current.content, [code]: { ...current.content[code], [key]: e.target.value } } }))} />
            </label>)}
          </div>)}
        </div>
        <button disabled={Boolean(busy)} onClick={saveSettings} className="kd9-btn-primary px-5 py-3 rounded-lg text-xs flex items-center gap-2 mt-5"><Save size={14}/> Guardar presentación</button>
      </section>
    </PageShell>
  );
};

export default HoroscopeManager;
